/**
 * SupportWidget — floating help bubble available on every page.
 *
 * Intercom-style bottom-right pill that expands into a panel with:
 *   • Category segmented control: Question / Bug / Suggestion
 *   • Message textarea
 *   • Email field (shown only for anonymous visitors; logged-in users
 *     have their email inferred server-side from the JWT)
 *   • Honeypot `website` field hidden off-screen for basic bot filtering
 *
 * Submissions hit POST /api/support/tickets which persists the ticket
 * in MongoDB and asynchronously emails contact@drone-maps.eu via Resend.
 *
 * Mounted globally in App.js so it survives route changes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageCircle,
  X,
  HelpCircle,
  Bug,
  Lightbulb,
  Send,
  CheckCircle2,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'question',   label: 'Question',    Icon: HelpCircle,  color: 'sky' },
  { value: 'bug',        label: 'Bug',         Icon: Bug,         color: 'red' },
  { value: 'suggestion', label: 'Suggestion',  Icon: Lightbulb,   color: 'amber' },
];

const CATEGORY_HINT = {
  question:   "Une question sur la réglementation, le fonctionnement, votre abonnement ?",
  bug:        "Décrivez ce qui ne marche pas et sur quelle page (ordi/mobile).",
  suggestion: "Une idée de fonctionnalité, une amélioration ?",
};

export default function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('question');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const textareaRef = useRef(null);

  // Detect the logged-in user (best-effort — token in localStorage matches
  // the rest of the app's auth pattern). We use this only to hide the
  // email field; server does the real trust check.
  const isAuthed = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(localStorage.getItem('dronemaps_token'));
  }, [open]);

  // Autofocus textarea when the panel opens.
  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 200);
    }
  }, [open]);

  // Close on ESC.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const resetAfterSuccess = () => {
    setMessage('');
    setSent(true);
    // Auto-close after a beat.
    setTimeout(() => {
      setSent(false);
      setOpen(false);
    }, 2600);
  };

  const canSubmit = message.trim().length >= 5 && (isAuthed || /\S+@\S+\.\S+/.test(email));

  const submit = async () => {
    if (!canSubmit || sending) return;
    setSending(true);
    try {
      const token = localStorage.getItem('dronemaps_token');
      await axios.post(
        `${API_URL}/api/support/tickets`,
        {
          category,
          message: message.trim(),
          email: isAuthed ? undefined : email.trim(),
          page_url: typeof window !== 'undefined' ? window.location.pathname + window.location.search : null,
          website, // honeypot
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      toast.success('Message envoyé — nous vous répondons vite !');
      resetAfterSuccess();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.error("Trop de messages envoyés — réessayez dans une heure.");
      } else if (detail) {
        toast.error(String(detail));
      } else {
        toast.error("Envoi impossible — vérifiez votre connexion.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating bubble — hidden while the panel is open on small screens */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="support-widget-open"
          aria-label="Poser une question ou signaler un problème"
          className="fixed bottom-5 right-5 z-[9998] group h-14 w-14 rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-xl shadow-sky-500/30 flex items-center justify-center transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-sky-300"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="pointer-events-none absolute right-full mr-3 hidden md:block whitespace-nowrap bg-slate-900 text-white text-xs font-medium px-3 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
            Une question ? Un souci ?
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="support-widget-panel"
          className="fixed bottom-5 right-5 z-[9999] w-[calc(100%-2.5rem)] max-w-sm rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
          role="dialog"
          aria-label="Aide et contact"
        >
          {/* Header */}
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold leading-tight">Une question ? Un souci ?</div>
              <div className="text-xs text-slate-300">On vous répond sous 24h.</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="support-widget-close"
              aria-label="Fermer"
              className="text-slate-300 hover:text-white p-1 rounded transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 space-y-3">
            {sent ? (
              <div className="py-6 text-center space-y-2" data-testid="support-widget-success">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                <div className="text-sm font-semibold text-slate-900">Message envoyé !</div>
                <div className="text-xs text-slate-500">Nous vous répondons dès que possible.</div>
              </div>
            ) : (
              <>
                {/* Category selector */}
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase mb-1.5">Type de message</div>
                  <div className="grid grid-cols-3 gap-1.5" role="radiogroup">
                    {CATEGORIES.map(({ value, label, Icon }) => {
                      const active = category === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setCategory(value)}
                          data-testid={`support-cat-${value}`}
                          className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-xs font-medium transition ${
                            active
                              ? 'border-sky-500 bg-sky-50 text-sky-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1.5" data-testid="support-cat-hint">
                    {CATEGORY_HINT[category]}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block" htmlFor="support-msg">
                    Votre message
                  </label>
                  <textarea
                    id="support-msg"
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    maxLength={5000}
                    data-testid="support-widget-message"
                    placeholder="Décrivez votre demande…"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400 resize-none"
                  />
                  <div className="text-[10px] text-slate-400 text-right mt-0.5">
                    {message.length}/5000
                  </div>
                </div>

                {/* Email — anonymous only */}
                {!isAuthed && (
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block" htmlFor="support-email">
                      Votre email (pour la réponse)
                    </label>
                    <input
                      id="support-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="support-widget-email"
                      placeholder="vous@exemple.com"
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                    />
                  </div>
                )}

                {/* Honeypot — visually hidden */}
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] w-px h-px"
                />

                {/* Submit */}
                <button
                  onClick={submit}
                  disabled={!canSubmit || sending}
                  data-testid="support-widget-submit"
                  className="w-full flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg transition"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Envoi…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Envoyer
                    </>
                  )}
                </button>

                <div className="text-[10px] text-slate-400 text-center pt-1">
                  Réponse à <a href="mailto:contact@drone-maps.eu" className="text-slate-500 hover:text-sky-600 underline">contact@drone-maps.eu</a>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
